import { BlockStack, InlineStack, Autocomplete, Icon } from "@medentic/polaris-react";
import { useCallback, useState, useEffect, useMemo } from "react";
import { SearchIcon } from "@medentic/polaris-icons";
import useTags from "@shared/hooks/common/useTags";
import Tag from "./Tag";
import { useTranslation } from "react-i18next";

type TagContext = "patients" | "attendees";

interface TagsListProps {
  selectedTags: string[];
  onTagsUpdate: (selectedTagIds: string[]) => void;
  hccId?: string;
  context?: TagContext;
}

function TagsList({
  selectedTags,
  onTagsUpdate,
  hccId,
  context = "patients",
}: TagsListProps) {
  const { t } = useTranslation();
  const { tags, isLoading } = useTags(hccId ?? "", context);
  const [queryValue, setQueryValue] = useState("");
  const [localSelectedTags, setLocalSelectedTags] =
    useState<string[]>(selectedTags);

  // Sync local state with parent prop if it changes
  useEffect(() => {
    setLocalSelectedTags(selectedTags);
  }, [selectedTags]);

  const removeTag = useCallback(
    (tagId: string) => {
      const updatedTags = localSelectedTags.filter((id) => id !== tagId);
      setLocalSelectedTags(updatedTags); // Optimistic update
      onTagsUpdate(updatedTags);
    },
    [localSelectedTags, onTagsUpdate]
  );

  const handleQueryChange = useCallback(
    (newValue: string) => setQueryValue(newValue),
    []
  );
  const handleQueryClear = useCallback(() => setQueryValue(""), []);

  // Filter tags based on search query
  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(queryValue.toLowerCase())
  );

  const tagMarkup = localSelectedTags.map((tagId) => {
    const tag = tags.find((t) => t.id === tagId);
    return (
      <Tag
        key={tagId}
        tagId={tagId}
        tagLabel={tag?.name ?? tagId}
        colorName={tag?.color ?? tagId}
        onRemove={removeTag}
      />
    );
  });

  const handleTagSelect = useCallback(
    (_selected: string[]) => {
      if (_selected.length > 0) {
        const selectedTagId = _selected[0];
        if (!localSelectedTags.includes(selectedTagId)) {
          const updatedTags = [...localSelectedTags, selectedTagId];
          setLocalSelectedTags(updatedTags); // Optimistic update
          onTagsUpdate(updatedTags);
        }
        setQueryValue("");
      }
    },
    [localSelectedTags, onTagsUpdate]
  );

  const options = useMemo(
    () =>
      filteredTags.map((tag) => ({
        label: tag.name,
        value: tag.id,
      })),
    [filteredTags]
  );

  const textField = useMemo(
    () => (
      <Autocomplete.TextField
        prefix={<Icon source={SearchIcon} />}
        label=""
        onChange={handleQueryChange}
        value={queryValue}
        placeholder={t("tags.searchPlaceholder")}
        clearButton
        onClearButtonClick={handleQueryClear}
        autoComplete="off"
      />
    ),
    [handleQueryChange, queryValue, handleQueryClear, t]
  );

  return (
    <BlockStack gap={"200"}>
      <Autocomplete
        loading={isLoading}
        options={options}
        selected={localSelectedTags}
        onSelect={handleTagSelect}
        textField={textField}
      />

      {/* Display selected tags */}
      <InlineStack gap={"200"}>{tagMarkup}</InlineStack>
    </BlockStack>
  );
}

export default TagsList;
